import React, { useState, useEffect, useCallback, Component } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { API_URL } from '../config/api';
import { 
  ArrowRight, ArrowLeft, Check, Wallet, TrendingUp, TrendingDown,
  CreditCard, Repeat, Calendar, Home, Car, Wifi, Smartphone, 
  ShoppingCart, Zap, Droplets, Trash2, Plus, Sparkles, Building2,
  Lightbulb, RefreshCw, Edit2, AlertTriangle, PiggyBank, Shield,
  ChevronDown, ChevronUp, Info, DollarSign, Clock, Target,
  CircleDollarSign, Settings2, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from 'sonner';
import { 
  FREQUENCIES,
  getFrequencyByValue, 
  getFrequencyLabel 
} from '../constants/frequencies';

const BACKEND_URL = API_URL;
const API = `${BACKEND_URL}/api`;

// =============================================================================
// ERROR BOUNDARY
// =============================================================================
class FoundationErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[FinancialFoundation] Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-center bg-red-50 rounded-xl border border-red-200">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-700 mb-2">Une erreur est survenue</h3>
          <p className="text-sm text-red-600 mb-4">{this.state.error?.message}</p>
          <Button 
            onClick={() => this.setState({ hasError: false, error: null })}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Réessayer
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

// =============================================================================
// CONSTANTS
// =============================================================================
// Utilisation du système de fréquences harmonisé
// Pour FinancialFoundation, on garde un sous-ensemble pratique (même que l'onboarding)
const FOUNDATION_FREQUENCIES = FREQUENCIES.filter(f => 
  ['mensuel', 'hebdomadaire', 'quinzaine', 'bimestriel', 'trimestriel', 'semestriel', 'annuel'].includes(f.value)
);

const INCOME_SUGGESTIONS = [
  { name: 'Salaire', icon: Wallet, category: 'Salaire' },
  { name: 'Allocations', icon: Heart, category: 'Allocations' },
  { name: 'Pension', icon: PiggyBank, category: 'Pension' },
  { name: 'Revenus locatifs', icon: Home, category: 'Revenus locatifs' },
  { name: 'Freelance', icon: Zap, category: 'Freelance' },
  { name: 'Prime', icon: Sparkles, category: 'Prime' },
];

const EXPENSE_SUGGESTIONS = [
  { name: 'Loyer', icon: Home, category: 'Logement' },
  { name: 'Électricité', icon: Zap, category: 'Énergie' },
  { name: 'Eau', icon: Droplets, category: 'Énergie' },
  { name: 'Gaz', icon: Zap, category: 'Énergie' },
  { name: 'Internet', icon: Wifi, category: 'Abonnements' },
  { name: 'Téléphone', icon: Smartphone, category: 'Abonnements' },
  { name: 'Assurance auto', icon: Car, category: 'Assurance' },
  { name: 'Assurance habitation', icon: Shield, category: 'Assurance' },
  { name: 'Crédit auto', icon: CreditCard, category: 'Crédit' },
  { name: 'Crédit immobilier', icon: Building2, category: 'Crédit' },
  { name: 'Courses', icon: ShoppingCart, category: 'Alimentation' },
  { name: 'Netflix', icon: Smartphone, category: 'Abonnements' },
  { name: 'Spotify', icon: Smartphone, category: 'Abonnements' },
  { name: 'Mutuelle', icon: Heart, category: 'Santé' },
  { name: 'Carburant', icon: Car, category: 'Transport' },
];

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const FinancialFoundationSetup = ({ calendar, onComplete, onDataChanged, isFirstSetup = false }) => {
  // State
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [existingFoundation, setExistingFoundation] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [categories, setCategories] = useState({ incomes: [], expenses: [] });

  // Form data
  const [initialBalance, setInitialBalance] = useState('');
  const [initialBalanceDate, setInitialBalanceDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [emergencyReservePercent, setEmergencyReservePercent] = useState(10);

  // New entry forms
  const [newIncome, setNewIncome] = useState({
    name: '', amount: '', day: 1, category: 'Salaire', frequency: 'mensuel'
  });
  const [newExpense, setNewExpense] = useState({
    name: '', amount: '', day: 1, category: 'Logement', frequency: 'mensuel', isEssential: true
  });

  // Steps configuration
  const steps = [
    { title: 'Bienvenue', icon: Building2, description: 'Configurer ta fondation' },
    { title: 'Solde initial', icon: CircleDollarSign, description: 'Ton point de départ' },
    { title: 'Revenus', icon: TrendingUp, description: 'Tes sources de revenus' },
    { title: 'Dépenses', icon: TrendingDown, description: 'Tes charges fixes' },
    { title: 'Récapitulatif', icon: Check, description: 'Validation finale' },
  ];

  // Guard: check calendar
  if (!calendar?.id) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Sélectionne un calendrier pour configurer ta fondation</p>
        </div>
      </div>
    );
  }

  // Load existing data
  const loadData = useCallback(async () => {
    if (!calendar?.id) return;
    
    setInitialLoading(true);
    try {
      // Load categories
      const [incCat, expCat] = await Promise.all([
        axios.get(`${API}/foundation/categories/incomes`),
        axios.get(`${API}/foundation/categories/expenses`)
      ]);
      setCategories({
        incomes: incCat.data.categories || [],
        expenses: expCat.data.categories || []
      });

      // Load existing foundation
      const response = await axios.get(
        `${API}/foundation/${calendar.id}`,
        { withCredentials: true }
      );

      if (response.data.exists && response.data.foundation) {
        setExistingFoundation(response.data.foundation);
        setInitialBalance(response.data.foundation.initial_balance?.toString() || '');
        setInitialBalanceDate(response.data.foundation.initial_balance_date || new Date().toISOString().split('T')[0]);
        setEmergencyReservePercent((response.data.foundation.emergency_reserve_percentage || 0.10) * 100);
        
        // Map incomes
        const mappedIncomes = (response.data.incomes || []).map(inc => ({
          id: inc.id,
          name: inc.name,
          amount: inc.amount,
          day: inc.day_of_month || 1,
          category: inc.category || 'Salaire',
          frequency: inc.frequency_type || 'mensuel',
          isExisting: true
        }));
        setIncomes(mappedIncomes);

        // Map expenses
        const mappedExpenses = (response.data.expenses || []).map(exp => ({
          id: exp.id,
          name: exp.name,
          amount: exp.amount,
          day: exp.day_of_month || 1,
          category: exp.category || 'Logement',
          frequency: exp.frequency_type || 'mensuel',
          isEssential: exp.is_essential !== false,
          isExisting: true
        }));
        setExpenses(mappedExpenses);
      } else {
        // Set initial balance from calendar
        setInitialBalance(calendar.initial_balance?.toString() || '0');
        setInitialBalanceDate(calendar.initial_balance_date || new Date().toISOString().split('T')[0]);
      }
    } catch (error) {
      console.error('Error loading foundation data:', error);
      // Set defaults from calendar
      setInitialBalance(calendar.initial_balance?.toString() || '0');
    } finally {
      setInitialLoading(false);
    }
  }, [calendar?.id, calendar?.initial_balance, calendar?.initial_balance_date]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Calculate totals
  const totalIncome = incomes.reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);
  const totalExpenses = expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  const monthlyBalance = totalIncome - totalExpenses;
  const emergencyReserve = monthlyBalance > 0 
    ? Math.min(Math.max(totalIncome * (emergencyReservePercent / 100), 20), 200)
    : 0;
  const availableBalance = monthlyBalance - emergencyReserve;

  // Add income
  const addIncome = () => {
    if (!newIncome.name || !newIncome.amount) {
      toast.error('Remplis le nom et le montant');
      return;
    }
    setIncomes(prev => [...prev, { 
      ...newIncome, 
      id: `new-${Date.now()}`,
      amount: parseFloat(newIncome.amount),
      day: parseInt(newIncome.day) || 1,
      isNew: true
    }]);
    setNewIncome({ name: '', amount: '', day: 1, category: 'Salaire', frequency: 'mensuel' });
    toast.success('Revenu ajouté');
  };

  // Add expense
  const addExpense = () => {
    if (!newExpense.name || !newExpense.amount) {
      toast.error('Remplis le nom et le montant');
      return;
    }
    setExpenses(prev => [...prev, { 
      ...newExpense, 
      id: `new-${Date.now()}`,
      amount: parseFloat(newExpense.amount),
      day: parseInt(newExpense.day) || 1,
      isNew: true
    }]);
    setNewExpense({ name: '', amount: '', day: 1, category: 'Logement', frequency: 'mensuel', isEssential: true });
    toast.success('Dépense ajoutée');
  };

  // Remove income
  const removeIncome = async (id) => {
    const income = incomes.find(i => i.id === id);
    if (income?.isExisting) {
      try {
        await axios.delete(
          `${API}/foundation/${calendar.id}/incomes/${id}`,
          { withCredentials: true }
        );
        toast.success('Revenu supprimé');
        // Trigger calendar refresh for immediate sync
        if (onDataChanged) onDataChanged();
      } catch (error) {
        console.error('Error deleting income:', error);
        toast.error('Erreur lors de la suppression');
        return;
      }
    }
    setIncomes(prev => prev.filter(i => i.id !== id));
  };

  // Remove expense
  const removeExpense = async (id) => {
    const expense = expenses.find(e => e.id === id);
    if (expense?.isExisting) {
      try {
        await axios.delete(
          `${API}/foundation/${calendar.id}/expenses/${id}`,
          { withCredentials: true }
        );
        toast.success('Dépense supprimée');
        // Trigger calendar refresh for immediate sync
        if (onDataChanged) onDataChanged();
      } catch (error) {
        console.error('Error deleting expense:', error);
        toast.error('Erreur lors de la suppression');
        return;
      }
    }
    setExpenses(prev => prev.filter(e => e.id !== id));
  };

  // Submit setup
  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Build setup payload avec système de fréquence harmonisé
      const newIncomes = incomes.filter(i => i.isNew).map(i => {
        const freqInfo = getFrequencyByValue(i.frequency);
        return {
          name: i.name,
          amount: parseFloat(i.amount),
          category: i.category,
          frequency_type: i.frequency,
          frequency_value: freqInfo?.unitValue || 1,
          frequency_unit: freqInfo?.unit || 'mois',
          day_of_month: parseInt(i.day) || 1,
          start_date: new Date().toISOString().split('T')[0]
        };
      });

      const newExpenses = expenses.filter(e => e.isNew).map(e => {
        const freqInfo = getFrequencyByValue(e.frequency);
        return {
          name: e.name,
          amount: parseFloat(e.amount),
          category: e.category,
          frequency_type: e.frequency,
          frequency_value: freqInfo?.unitValue || 1,
          frequency_unit: freqInfo?.unit || 'mois',
          day_of_month: parseInt(e.day) || 1,
          start_date: new Date().toISOString().split('T')[0],
          is_essential: e.isEssential
        };
      });

      const payload = {
        initial_balance: parseFloat(initialBalance) || 0,
        initial_balance_date: initialBalanceDate,
        incomes: newIncomes,
        expenses: newExpenses,
        emergency_reserve_percentage: emergencyReservePercent / 100
      };

      await axios.post(
        `${API}/foundation/${calendar.id}/setup`,
        payload,
        { withCredentials: true }
      );

      // Generate recurring operations for current month
      const today = new Date();
      await axios.post(
        `${API}/generate-recurring?calendar_id=${calendar.id}&year=${today.getFullYear()}&month=${today.getMonth() + 1}`,
        {},
        { withCredentials: true }
      );

      setShowSuccess(true);
      toast.success('Ta fondation financière est configurée !');

      // Callback after delay
      setTimeout(() => {
        setShowSuccess(false);
        setIsEditing(false);
        if (onComplete) onComplete();
        // Trigger calendar refresh for immediate sync
        if (onDataChanged) onDataChanged();
        loadData();
      }, 2500);

    } catch (error) {
      console.error('Error saving foundation:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Chargement de ta fondation...</p>
        </div>
      </div>
    );
  }

  // Success state
  if (showSuccess) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center min-h-[400px] text-center"
      >
        <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center mb-6 shadow-lg">
          <Check className="h-10 w-10 text-white" />
        </div>
        <h2 className="text-2xl font-bold mb-2">Parfait !</h2>
        <p className="text-muted-foreground">Ta fondation financière est prête.</p>
        <p className="text-sm text-muted-foreground mt-2">Tes opérations récurrentes sont intégrées au calendrier.</p>
      </motion.div>
    );
  }

  // Summary view (when foundation exists and not editing)
  if (existingFoundation && !isEditing && step === 0 && !isFirstSetup) {
    return (
      <div className="space-y-6" data-testid="foundation-summary">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-50 to-gray-100 rounded-2xl p-5 border border-slate-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground mb-1">Ma Base Financière</h2>
              <p className="text-sm text-muted-foreground">
                Ton socle financier est configuré. Voici le résumé de ta situation.
              </p>
            </div>
          </div>
        </div>

        {/* Key metrics */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              <span className="text-sm font-medium text-emerald-800">Revenus</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600">+{totalIncome.toFixed(0)}€</p>
            <p className="text-xs text-emerald-600/70">{incomes.length} source{incomes.length > 1 ? 's' : ''} / mois</p>
          </div>
          
          <div className="bg-rose-50 rounded-xl p-4 border border-rose-200">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="h-5 w-5 text-rose-600" />
              <span className="text-sm font-medium text-rose-800">Dépenses</span>
            </div>
            <p className="text-2xl font-bold text-rose-600">-{totalExpenses.toFixed(0)}€</p>
            <p className="text-xs text-rose-600/70">{expenses.length} charge{expenses.length > 1 ? 's' : ''} / mois</p>
          </div>
        </div>

        {/* Balance and reserve */}
        <div className="space-y-3">
          <div className={`rounded-xl p-4 border ${monthlyBalance >= 0 ? 'bg-teal-50 border-teal-200' : 'bg-orange-50 border-orange-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wallet className="h-5 w-5 text-teal-600" />
                <span className="text-sm font-medium">Reste à vivre</span>
              </div>
              <span className={`text-xl font-bold ${monthlyBalance >= 0 ? 'text-teal-600' : 'text-orange-600'}`}>
                {monthlyBalance >= 0 ? '+' : ''}{monthlyBalance.toFixed(0)}€/mois
              </span>
            </div>
          </div>

          {emergencyReserve > 0 && (
            <div className="rounded-xl p-4 border bg-amber-50 border-amber-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-amber-600" />
                  <span className="text-sm font-medium">Réserve de sécurité</span>
                </div>
                <span className="text-lg font-bold text-amber-600">{emergencyReserve.toFixed(0)}€/mois</span>
              </div>
            </div>
          )}

          <div className="rounded-xl p-4 border bg-blue-50 border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium">Disponible réel</span>
              </div>
              <span className="text-xl font-bold text-blue-600">{availableBalance.toFixed(0)}€/mois</span>
            </div>
          </div>
        </div>

        {/* Initial balance info */}
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <div className="flex items-center gap-2 mb-1">
            <CircleDollarSign className="h-4 w-4 text-slate-500" />
            <span className="text-sm text-slate-600">Solde initial au {new Date(initialBalanceDate).toLocaleDateString('fr-FR')}</span>
          </div>
          <p className="text-lg font-semibold">{parseFloat(initialBalance || 0).toFixed(2)}€</p>
        </div>

        {/* Tip */}
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-4 border border-amber-200">
          <div className="flex gap-3">
            <Lightbulb className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              <strong>Astuce :</strong> Ta base financière alimente automatiquement le calendrier. 
              Modifie-la à tout moment pour ajuster tes projections.
            </p>
          </div>
        </div>

        {/* Edit button */}
        <Button 
          onClick={() => setIsEditing(true)}
          className="w-full h-12 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white rounded-xl"
          data-testid="foundation-edit-btn"
        >
          <Edit2 className="h-4 w-4 mr-2" />
          Modifier ma base financière
        </Button>
      </div>
    );
  }

  // Wizard steps
  const renderStep = () => {
    switch (step) {
      case 0: // Welcome
        return (
          <div className="space-y-6 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto shadow-xl">
              <Building2 className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">
              {existingFoundation ? 'Modifier ma Base Financière' : 'Configurer ma Base Financière'}
            </h2>
            <p className="text-muted-foreground">
              {existingFoundation 
                ? 'Ajuste tes revenus et dépenses pour garder ton budget à jour.'
                : 'Définis ton solde initial, tes revenus et tes charges fixes pour démarrer avec un budget structuré.'
              }
            </p>
            
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-4 border border-amber-200 text-left">
              <div className="flex gap-3">
                <Lightbulb className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <strong>Qu'est-ce que la Base Financière ?</strong>
                  <ul className="mt-2 space-y-1 list-disc list-inside">
                    <li>Ton solde de départ</li>
                    <li>Tes revenus récurrents (salaire, allocations...)</li>
                    <li>Tes charges fixes (loyer, abonnements...)</li>
                    <li>Une réserve de sécurité automatique</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 1: // Initial balance
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <CircleDollarSign className="h-10 w-10 text-blue-500 mx-auto mb-2" />
              <h2 className="text-xl font-bold text-foreground">Ton solde initial</h2>
              <p className="text-sm text-muted-foreground">Le point de départ de ton budget</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Solde actuel de ton compte (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="Ex: 1500.00"
                  value={initialBalance}
                  onChange={(e) => setInitialBalance(e.target.value)}
                  className="h-12 text-lg"
                  data-testid="initial-balance-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Indique le solde réel de ton compte aujourd'hui
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium">Date du solde</Label>
                <Input
                  type="date"
                  value={initialBalanceDate}
                  onChange={(e) => setInitialBalanceDate(e.target.value)}
                  className="h-12"
                  data-testid="initial-balance-date-input"
                />
              </div>

              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <div className="flex gap-3">
                  <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <p className="text-sm text-blue-800">
                    Ce solde sera utilisé comme point de départ pour calculer tes projections financières.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 2: // Incomes
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <TrendingUp className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
              <h2 className="text-xl font-bold text-foreground">Tes revenus</h2>
              <p className="text-sm text-muted-foreground">Ajoute tes sources de revenus récurrentes</p>
            </div>

            {/* Suggestions */}
            <div className="flex flex-wrap gap-2 mb-4">
              {INCOME_SUGGESTIONS.map((sug, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setNewIncome(prev => ({ ...prev, name: sug.name, category: sug.category }))}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                    newIncome.name === sug.name 
                      ? 'bg-emerald-200 text-emerald-800 border-emerald-400' 
                      : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                  }`}
                  data-testid={`income-suggestion-${idx}`}
                >
                  {sug.name}
                </button>
              ))}
            </div>

            {/* List */}
            {incomes.length > 0 && (
              <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                {incomes.map((income) => (
                  <div key={income.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex-1">
                      <span className="font-medium">{income.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        le {income.day} • {getFrequencyLabel(income.frequency, 'mois', 1)}
                      </span>
                      {income.isNew && <span className="text-xs text-emerald-600 ml-2">(nouveau)</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-emerald-600">+{parseFloat(income.amount).toFixed(0)}€</span>
                      <button 
                        onClick={() => removeIncome(income.id)} 
                        className="text-gray-400 hover:text-red-500"
                        data-testid={`remove-income-${income.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add form */}
            <div className="bg-muted/50 rounded-xl p-4 border">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <Label className="text-xs">Nom</Label>
                  <Input
                    placeholder="Ex: Salaire"
                    value={newIncome.name}
                    onChange={(e) => setNewIncome(prev => ({ ...prev, name: e.target.value }))}
                    className="h-9"
                    data-testid="income-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Montant (€)</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={newIncome.amount}
                    onChange={(e) => setNewIncome(prev => ({ ...prev, amount: e.target.value }))}
                    className="h-9"
                    data-testid="income-amount-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div>
                  <Label className="text-xs">Jour du mois</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={newIncome.day}
                    onChange={(e) => setNewIncome(prev => ({ ...prev, day: e.target.value }))}
                    className="h-9"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Fréquence</Label>
                  <Select
                    value={newIncome.frequency}
                    onValueChange={(v) => setNewIncome(prev => ({ ...prev, frequency: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FOUNDATION_FREQUENCIES.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button 
                onClick={addIncome}
                disabled={!newIncome.name || !newIncome.amount}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="add-income-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter ce revenu
              </Button>
            </div>

            <div className="text-center pt-2">
              <span className="text-sm text-muted-foreground">Total revenus : </span>
              <span className="font-bold text-emerald-600">{totalIncome.toFixed(2)}€/mois</span>
            </div>
          </div>
        );

      case 3: // Expenses
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <TrendingDown className="h-10 w-10 text-rose-500 mx-auto mb-2" />
              <h2 className="text-xl font-bold text-foreground">Tes dépenses fixes</h2>
              <p className="text-sm text-muted-foreground">Ajoute tes charges récurrentes</p>
            </div>

            {/* Suggestions */}
            <div className="flex flex-wrap gap-2 mb-4">
              {EXPENSE_SUGGESTIONS.slice(0, 10).map((sug, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setNewExpense(prev => ({ ...prev, name: sug.name, category: sug.category }))}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-all ${
                    newExpense.name === sug.name 
                      ? 'bg-rose-200 text-rose-800 border-rose-400' 
                      : 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
                  }`}
                  data-testid={`expense-suggestion-${idx}`}
                >
                  {sug.name}
                </button>
              ))}
            </div>

            {/* List */}
            {expenses.length > 0 && (
              <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
                {expenses.map((expense) => (
                  <div key={expense.id} className="flex items-center justify-between p-3 bg-rose-50 rounded-lg border border-rose-200">
                    <div className="flex-1">
                      <span className="font-medium">{expense.name}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        le {expense.day} • {getFrequencyLabel(expense.frequency, 'mois', 1)}
                      </span>
                      {expense.isNew && <span className="text-xs text-rose-600 ml-2">(nouveau)</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-rose-600">-{parseFloat(expense.amount).toFixed(0)}€</span>
                      <button 
                        onClick={() => removeExpense(expense.id)} 
                        className="text-gray-400 hover:text-red-500"
                        data-testid={`remove-expense-${expense.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add form */}
            <div className="bg-muted/50 rounded-xl p-4 border">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <Label className="text-xs">Nom</Label>
                  <Input
                    placeholder="Ex: Loyer"
                    value={newExpense.name}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, name: e.target.value }))}
                    className="h-9"
                    data-testid="expense-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs">Montant (€)</Label>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, amount: e.target.value }))}
                    className="h-9"
                    data-testid="expense-amount-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div>
                  <Label className="text-xs">Jour du mois</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={newExpense.day}
                    onChange={(e) => setNewExpense(prev => ({ ...prev, day: e.target.value }))}
                    className="h-9"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Fréquence</Label>
                  <Select
                    value={newExpense.frequency}
                    onValueChange={(v) => setNewExpense(prev => ({ ...prev, frequency: v }))}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FOUNDATION_FREQUENCIES.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button 
                onClick={addExpense}
                disabled={!newExpense.name || !newExpense.amount}
                className="w-full bg-rose-600 hover:bg-rose-700"
                data-testid="add-expense-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Ajouter cette dépense
              </Button>
            </div>

            <div className="text-center pt-2">
              <span className="text-sm text-muted-foreground">Total dépenses : </span>
              <span className="font-bold text-rose-600">{totalExpenses.toFixed(2)}€/mois</span>
            </div>
          </div>
        );

      case 4: // Summary
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <Check className="h-10 w-10 text-teal-500 mx-auto mb-2" />
              <h2 className="text-xl font-bold text-foreground">Récapitulatif</h2>
              <p className="text-sm text-muted-foreground">Vérifie avant de valider</p>
            </div>

            {/* Initial balance */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CircleDollarSign className="h-5 w-5 text-slate-500" />
                  <span className="text-sm font-medium">Solde initial</span>
                </div>
                <span className="text-lg font-bold">{parseFloat(initialBalance || 0).toFixed(2)}€</span>
              </div>
            </div>

            {/* Totals */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-800">Revenus</span>
                </div>
                <p className="text-2xl font-bold text-emerald-600">+{totalIncome.toFixed(0)}€</p>
                <p className="text-xs text-emerald-600/70">{incomes.length} entrée{incomes.length > 1 ? 's' : ''}</p>
              </div>
              
              <div className="bg-rose-50 rounded-xl p-4 border border-rose-200">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="h-5 w-5 text-rose-600" />
                  <span className="text-sm font-medium text-rose-800">Dépenses</span>
                </div>
                <p className="text-2xl font-bold text-rose-600">-{totalExpenses.toFixed(0)}€</p>
                <p className="text-xs text-rose-600/70">{expenses.length} charge{expenses.length > 1 ? 's' : ''}</p>
              </div>
            </div>

            {/* Balance */}
            <div className={`rounded-xl p-4 border ${monthlyBalance >= 0 ? 'bg-teal-50 border-teal-200' : 'bg-orange-50 border-orange-200'}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Reste à vivre</span>
                <span className={`text-xl font-bold ${monthlyBalance >= 0 ? 'text-teal-600' : 'text-orange-600'}`}>
                  {monthlyBalance >= 0 ? '+' : ''}{monthlyBalance.toFixed(0)}€/mois
                </span>
              </div>
            </div>

            {/* Emergency reserve */}
            {emergencyReserve > 0 && (
              <div className="rounded-xl p-4 border bg-amber-50 border-amber-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-amber-600" />
                    <span className="text-sm font-medium">Réserve de sécurité ({emergencyReservePercent}%)</span>
                  </div>
                  <span className="text-lg font-bold text-amber-600">{emergencyReserve.toFixed(0)}€/mois</span>
                </div>
              </div>
            )}

            {/* Available */}
            <div className="rounded-xl p-4 border bg-blue-50 border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium">Disponible réel</span>
                </div>
                <span className="text-xl font-bold text-blue-600">{availableBalance.toFixed(0)}€/mois</span>
              </div>
            </div>

            {/* New items count */}
            {(incomes.filter(i => i.isNew).length > 0 || expenses.filter(e => e.isNew).length > 0) && (
              <div className="text-sm text-muted-foreground space-y-1">
                {incomes.filter(i => i.isNew).length > 0 && (
                  <p><strong>{incomes.filter(i => i.isNew).length}</strong> nouveau(x) revenu(s) à créer</p>
                )}
                {expenses.filter(e => e.isNew).length > 0 && (
                  <p><strong>{expenses.filter(e => e.isNew).length}</strong> nouvelle(s) dépense(s) à créer</p>
                )}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6" data-testid="foundation-setup-wizard">
      {/* Progress bar */}
      {(isEditing || !existingFoundation || isFirstSetup) && step > 0 && (
        <div className="flex gap-2">
          {steps.map((s, idx) => (
            <div
              key={idx}
              className={`flex-1 h-1.5 rounded-full transition-all ${
                idx <= step ? 'bg-gradient-to-r from-teal-500 to-emerald-600' : 'bg-muted'
              }`}
            />
          ))}
        </div>
      )}

      {/* Step content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {renderStep()}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      {(isEditing || !existingFoundation || isFirstSetup || step > 0) && (
        <div className="flex gap-3">
          {step > 0 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1 h-11 rounded-xl"
              data-testid="foundation-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
          )}
          
          <Button
            onClick={() => {
              if (step === 4) {
                handleSubmit();
              } else {
                setStep(step + 1);
              }
            }}
            disabled={loading}
            className="flex-1 h-11 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 rounded-xl"
            data-testid="foundation-next-btn"
          >
            {loading ? (
              'Enregistrement...'
            ) : step === 4 ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Valider ma base financière
              </>
            ) : (
              <>
                Continuer
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      )}

      {/* Cancel button when editing */}
      {isEditing && step === 0 && (
        <Button
          variant="ghost"
          onClick={() => {
            setIsEditing(false);
            loadData();
          }}
          className="w-full h-10 text-muted-foreground"
        >
          Annuler
        </Button>
      )}
    </div>
  );
};

// Export with error boundary
const FinancialFoundation = (props) => (
  <FoundationErrorBoundary>
    <FinancialFoundationSetup {...props} />
  </FoundationErrorBoundary>
);

export default FinancialFoundation;
